import React, {
  useState,
  ChangeEvent,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Box, IconButton, Text } from "@chakra-ui/react";
import { FaImage, FaPaperclip } from "react-icons/fa";
import { formatFileSize } from "../utils/fileUtils";

interface FileInputProps {
  onFilesSelected: (files: File[]) => void;
  selectedFiles?: File[]; // Make it controlled
  maxFiles?: number;
  maxTotalSize: number; // in bytes - required prop
  acceptedImageTypes?: string[];
  acceptedFileTypes?: string[];
}

export interface FileInputRef {
  setError: (error: string) => void;
}

const FileInput = forwardRef<FileInputRef, FileInputProps>(
  (
    {
      onFilesSelected,
      selectedFiles = [],
      maxFiles = 10,
      maxTotalSize,
      acceptedImageTypes = ["image/*"],
      acceptedFileTypes = [
        "application/pdf",
        ".doc",
        ".docx",
        ".txt",
        ".xls",
        ".xlsx",
        ".csv",
      ],
    },
    ref
  ) => {
    const [error, setError] = useState<string | null>(null);
    const imageInputRef = useRef<HTMLInputElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const validateFiles = (
      files: File[]
    ): { validFiles: File[]; error: string | null } => {
      // Check file count
      if (files.length > maxFiles) {
        return {
          validFiles: [],
          error: `Cannot attach more than ${maxFiles} files`,
        };
      }

      // Check total size
      const totalSize = files.reduce((sum, file) => sum + file.size, 0);
      if (totalSize > maxTotalSize) {
        return {
          validFiles: [],
          error: `Total file size cannot exceed ${formatFileSize(
            maxTotalSize
          )}`,
        };
      }

      return { validFiles: files, error: null };
    };

    const handleFileChange = (
      event: ChangeEvent<HTMLInputElement>
    ) => {
      const files = Array.from(event.target.files || []);
      if (files.length === 0) return;

      const newFiles = [...selectedFiles, ...files];
      const { validFiles, error } = validateFiles(newFiles);

      if (error) {
        setError(error);
        // Clear the input
        event.target.value = "";
        return;
      }

      setError(null);
      onFilesSelected(validFiles);

      // Clear the input
      event.target.value = "";
    };

    const handleImageClick = () => {
      imageInputRef.current?.click();
    };

    const handleFileClick = () => {
      fileInputRef.current?.click();
    };

    useImperativeHandle(ref, () => ({
      setError: (errorMessage: string) => {
        setError(errorMessage);
        setTimeout(() => setError(null), 10000);
      },
    }));

    return (
      <Box>
        <input
          ref={imageInputRef}
          type="file"
          multiple
          accept={acceptedImageTypes.join(",")}
          onChange={handleFileChange}
          style={{ display: "none" }}
        />
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={acceptedFileTypes.join(",")}
          onChange={handleFileChange}
          style={{ display: "none" }}
        />

        {/* Attachment buttons */}
        <Box display="flex" gap={2} alignItems="center">
          <IconButton
            aria-label="Attach image"
            icon={<FaImage />}
            size="sm"
            variant="ghost"
            color="gray.600"
            _hover={{ bg: "gray.100" }}
            onClick={handleImageClick}
            isDisabled={selectedFiles.length >= maxFiles}
          />
          <IconButton
            aria-label="Attach file"
            icon={<FaPaperclip />}
            size="sm"
            variant="ghost"
            color="gray.600"
            _hover={{ bg: "gray.100" }}
            onClick={handleFileClick}
            isDisabled={selectedFiles.length >= maxFiles}
          />
          {error && (
            <Text fontSize="xs" color="red.500">
              {error}
            </Text>
          )}
        </Box>
      </Box>
    );
  }
);

FileInput.displayName = "FileInput";

export default FileInput;
